import styled from 'styled-components'
import { BaseTextArea } from 'components/Inputs/Base'
import { NumericFormat } from 'react-number-format'

/*
  Estilos da área de repasse automático do admin.

  Local, como no resto do repositório: nenhuma página importa o `styles.js` de outra, e abrir
  essa porta faria uma mudança visual no Cutilei Pay do salão mexer, sem aviso, na tela do
  financeiro. O preço é a repetição das peças de dinheiro, que é pequeno e explícito.
*/

const SURFACE = '#FFFFFF'
const SURFACE_LINE = '#EDEDED'
const CARD_SHADOW = '0px 1px 10px -7px rgba(0, 0, 0, 0.6)'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 30px;
`

export const Content = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 0px 20px 40px;
`

export const FilterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`

export const Card = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${SURFACE};
  border: 1px solid ${SURFACE_LINE};
  box-shadow: ${CARD_SHADOW};
  border-radius: 15px;
  padding: 16px;
  gap: ${({ gap }) => gap ?? 14}px;
`

export const CardHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`

export const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #252525;
  margin: 0px;
`

export const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #252525;
  margin: 0px 0px 10px;
  align-self: center;
`

export const SubTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #252525;
  margin: 0px;
`

/*
  Grade dos totais. `auto-fit` em vez de um número fixo de colunas porque a quantidade de
  situações que aparecem depende do que existe na base: num ambiente novo há duas, e em
  produção cinco.
*/
export const SummaryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
`

export const SummaryCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid ${SURFACE_LINE};
  border-radius: 15px;
  background-color: #FAFAFA;
`

export const SummaryLabel = styled.span`
  font-size: 12px;
  color: #808080;
`

export const SummaryCount = styled.span`
  font-size: 12px;
  color: #909090;
`

export const DataList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const DataRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  padding: 11px 0px;
  border-bottom: 1px solid ${SURFACE_LINE};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0px;
  }

  &:first-child {
    padding-top: 0px;
  }
`

export const DataLabel = styled.span`
  font-size: 13px;
  color: #808080;
`

export const DataValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #252525;
  text-align: right;
  word-break: break-word;
`

export const PayoutList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const PayoutRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 12px 0px;
  border-bottom: 1px solid ${SURFACE_LINE};
  cursor: pointer;
  transition: background-color 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #FAFAFA;
  }
`

export const PayoutInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0px;

  b {
    font-size: 14px;
    font-weight: 500;
    color: #252525;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const PayoutMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  span {
    font-size: 12px;
    color: #909090;
  }
`

export const PayoutSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: ${({ align }) => align || 'flex-start'};
  gap: 6px;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ color }) => color || '#FF9800'};
  background-color: ${({ color }) => `${color || '#FF9800'}1A`};
`

/*
  Atalhos da operação: a fila de revisão e os recebíveis parados por falta de conta. São os
  dois recortes que se olham todo dia, e obrigar a montá-los no seletor de situação toda vez
  esconderia o que precisa ficar visível.
*/
export const ShortcutRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`

export const Shortcut = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 15px;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  color: ${({ active }) => (active ? '#252525' : '#606060')};
  background-color: ${({ active }) => (active ? '#FFC1001A' : SURFACE)};
  border: 1px solid ${({ active }) => (active ? '#FFC100' : SURFACE_LINE)};

  &:hover {
    border-color: #FFC100;
  }
`

export const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap ?? 5}px;
`

export const CheckRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

export const Label = styled.label`
  font-size: 14px;
  color: #252525;
`

export const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #606060;
`

export const EmptyState = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 20px;
  color: #B0B0B0;

  b {
    font-size: 14px;
    font-weight: 500;
    color: #606060;
  }

  p {
    font-size: 13px;
    color: #909090;
    margin: 0px;
    text-align: center;
  }
`

export const InfoText = styled.p`
  color: #777777;
  font-size: 13px;
  text-align: ${({ align }) => align || 'left'};
  line-height: 1.45;
  margin: 0px;
`

export const WarningText = styled.p`
  margin: 0px;
  text-align: ${({ align }) => align || 'left'};
  font-size: 13px;
  color: #FF3939;
`

/*
  Aviso de configuração incompleta — e não um erro, porque nada falhou. Serve ao caso do
  `PAYOUT_START_DATE` ausente, em que a chave geral pode estar ligada e ainda assim nenhum
  recebível ser gerado: sem este bloco a tela diria "ativo" para uma máquina parada.
*/
export const NoticeCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 15px;
  border-radius: 15px;
  color: #8A6100;
  background-color: #FFF6DC;
  border: 1px solid #FFE08A;

  p {
    margin: 0px;
    font-size: 13px;
    line-height: 1.45;
  }
`

export const TextArea = styled(BaseTextArea)`
  width: 100%;
`

export const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`

/* ---- Dinheiro ---- */

const moneyFormat = props => ({
  value: Number (props.value).toFixed (2).replace ('.', ','),
  thousandSeparator: '.',
  decimalSeparator: ',',
  displayType: 'text',
  prefix: 'R$ '
})

export const MoneyText = styled(NumericFormat).attrs (moneyFormat)`
  color: #252525;
  font-size: 14px;
  font-weight: 500;
`

export const SummaryValue = styled(NumericFormat).attrs (moneyFormat)`
  color: #252525;
  font-size: 18px;
  font-weight: 600;
`
